import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  Skeleton,
} from '@mui/material';
import { getJobOpeningDetails } from '../../../../../utils/api_service/JobOpenings';
import { Applicant, PieItem } from '../../../../../utils/types/JobOpenings';
import { BarChart } from '../../ui/Charts/BarChart';
import { NumberInfo } from '../../ui/Charts/NumberInfo';

export type OverviewProps = {
  candidates: Applicant[];
  loading: boolean;
  jobOpeningId: string;
};

export const Overview: React.FC<OverviewProps> = ({
  candidates,
  loading,
  jobOpeningId,
}) => {
  const [deadline, setDeadline] = useState<number>(0);

  useEffect(() => {
    getDetailsDate();
  }, []);

  const getDetailsDate = async () => {
    try {
      const res = await getJobOpeningDetails(jobOpeningId);
      if (res.ok) {
        const data = await res.json();
        const dl = data.jobRequisition.deadline;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const deadlineDate = new Date(dl * 1000);
        deadlineDate.setHours(0, 0, 0, 0);

        const msPerDay = 1000 * 60 * 60 * 24;
        const daysRemaining = Math.max(
          0,
          Math.floor((deadlineDate.getTime() - today.getTime()) / msPerDay),
        );

        setDeadline(daysRemaining);
      }
    } catch {
      setDeadline(0);
    }
  };

  const stats = [
    { label: 'Applicants', value: candidates.length },
    { label: 'Days to deadline', value: deadline },
  ];

  const genderPieData = (applicants: Applicant[]): PieItem[] => {
    const genderGroups: Record<string, number> = {
      Male: 0,
      Female: 0,
      'Non-binary': 0,
      Other: 0,
    };

    const genderColors: Record<string, { bg: string; text: string }> = {
      Male: { bg: 'rgba(34, 197, 94, 0.1)', text: '#15803d' },
      Female: { bg: 'rgba(234, 179, 8, 0.1)', text: '#a16207' },
      'Non-binary': { bg: 'rgba(244, 63, 94, 0.1)', text: '#be123c' },
      Other: { bg: 'rgba(59, 130, 246, 0.1)', text: '#1d4ed8' },
    };

    applicants.forEach((applicant) => {
      if (applicant.gender === 'Other') {
        genderGroups['Other']++;
      } else if (applicant.gender === 'Male') {
        genderGroups['Male']++;
      } else if (applicant.gender === 'Female') {
        genderGroups['Female']++;
      } else if (applicant.gender === 'Non-binary') {
        genderGroups['Non-binary']++;
      }
    });

    return Object.entries(genderGroups).map(([name, value]) => ({
      name: name === 'Non-binary' ? 'Non Binary' : name,
      value,
      color: `${genderColors[name]?.bg} | ${genderColors[name]?.text}`,
    }));
  };

  const genderStats = genderPieData(candidates);

  const generateAgePieData = (applicants: Applicant[]): PieItem[] => {
    const ageGroups: Record<string, number> = {
      '18-25': 0,
      '25-35': 0,
      '35-50': 0,
      '50-60': 0,
      '60+': 0,
      'Unknown': 0,
    };

    const groupColors: Record<string, string> = {
      '18-25': '#22c55e',
      '25-35': '#3b82f6',
      '35-50': '#f43f5e',
      '50-60': '#78716c',
      '60+': '#fde047',
      'Unknown': '#374151',
    };

    applicants.forEach((applicant) => {
      if (applicant.age === null) {
        ageGroups['Unknown']++;
      } else if (applicant.age >= 18 && applicant.age <= 25) {
        ageGroups['18-25']++;
      } else if (applicant.age > 25 && applicant.age <= 35) {
        ageGroups['25-35']++;
      } else if (applicant.age > 35 && applicant.age <= 50) {
        ageGroups['35-50']++;
      } else if (applicant.age > 50 && applicant.age <= 60) {
        ageGroups['50-60']++;
      } else if (applicant.age > 60) {
        ageGroups['60+']++;
      }
    });

    return Object.entries(ageGroups).map(([name, value]) => ({
      name,
      value,
      color: groupColors[name],
    }));
  };

  const ageRange = generateAgePieData(candidates);

  const categorizeExperience = (data: Applicant[]): number[] => {
    const buckets = [0, 0, 0, 0, 0, 0, 0];

    data.forEach((item) => {
      const years = item.value[1];

      if (years >= 0 && years < 1) {
        buckets[0]++;
      } else if (years >= 1 && years <= 2) {
        buckets[1]++;
      } else if (years >= 3 && years <= 5) {
        buckets[2]++;
      } else if (years >= 6 && years <= 10) {
        buckets[3]++;
      } else if (years >= 11 && years <= 15) {
        buckets[4]++;
      } else if (years >= 16 && years <= 20) {
        buckets[5]++;
      } else if (years > 20) {
        buckets[6]++;
      }
    });

    return buckets;
  };

  const yAxisValues = categorizeExperience(candidates);

  return (
    <Grid container spacing={2} sx={{ py: 2 }}>
      {/* Basic Stats */}
      {stats.map((stat) => (
        <Grid item xs={12} sm={6} xl={3} key={stat.label}>
          <Paper
            variant='outlined'
            sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <NumberInfo value={stat.value} name={stat.label} loading={loading} />
          </Paper>
        </Grid>
      ))}

      {/* Years of Experience Chart */}
      <Grid item xs={12} sm={12} xl={6}>
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
              Experience Distribution
            </Typography>
            <Chip
              label={`${candidates.length} total`}
              size='small'
              sx={{
                bgcolor: 'success.light',
                color: 'success.dark',
                fontWeight: 600,
                fontSize: '0.6875rem',
              }}
            />
          </Box>
          <BarChart
            name='Years Experience'
            data={yAxisValues}
            loading={loading}
          />
        </Paper>
      </Grid>

      {/* Gender Stats */}
      <Grid item xs={12} sm={6} xl={3}>
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5 }}>
            Gender
          </Typography>
          {loading ? (
            <Grid container spacing={1}>
              {[...Array(2)].map((_, idx) => (
                <Grid item xs={6} key={idx}>
                  <Skeleton variant='rounded' height={36} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={1}>
              {genderStats.map((item) => {
                const [bg, text] = (item.color || '').split(' | ');
                return (
                  <Grid item xs={6} key={item.name}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        bgcolor: bg || 'action.hover',
                        color: text || 'text.primary',
                        borderRadius: 1.5,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        textAlign: 'center',
                      }}
                    >
                      {item.value} {item.name}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      </Grid>

      {/* Age Stats */}
      <Grid item xs={12} sm={6} xl={3}>
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5 }}>
            Age
          </Typography>
          {loading ? (
            <Grid container spacing={1}>
              {[...Array(4)].map((_, idx) => (
                <Grid item xs={6} key={idx}>
                  <Skeleton variant='rounded' height={24} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={1}>
              {ageRange.map((item) => (
                <Grid item xs={12} sm={6} xl={12} key={item.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: item.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {item.name}, {item.value || 0}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Overview;
